import { useFormikContext } from "formik";
import React, { useState } from "react";
import { PremiumStructure } from "../../../../common/interfaces/enums/PremiumStructure";
import ProductType from "../../../../common/interfaces/enums/ProductType";
import Icon from "../../../../common/components/Icon/Icon";
import PremiumStructuresModal from "../../Modals/PremiumStructuresModal/PremiumStructuresModal";
import Button from "../../../../common/components/Forms/Button/Button";
import Toggle from "../../../../common/components/Forms/Toggle/Toggle";

// styles
import cx from "classnames";
import styles from "./GlobalProductFormLife.module.scss";

const levelLifeOptions = [
    { id: PremiumStructure.LevelTenYears, label: "10 years" },
    { id: PremiumStructure.LevelToAgeSixtyFive, label: "To age 65" },
    { id: PremiumStructure.LevelToAgeSeventy, label: "To age 70" },
    { id: PremiumStructure.LevelToAgeEighty, label: "To age 80" }
];

const premiumStructureInfo = {
    stepped: [
        "Most commonly chosen structure",
        "Cost increases with age",
        "Lower cost in the short term"
    ],
    level: [
        "Guaranteed not to increase with age",
        "More affordable in the long term",
        "Peace of mind for your budget"
    ]
};

const GlobalProductFormLife: React.FC = () => {
    const { values, setFieldValue } = useFormikContext();

    // State
    const [isOpenPremiumStructuresModal, setIsOpenPremiumStructuresModal] = useState(
        false
    );
    const [selectedPremiumStructure, setSelectedPremiumStructure] = useState(
        values[ProductType.Life]?.premiumStructure
    );

    const isLevelLifeSelected =
        selectedPremiumStructure !== PremiumStructure.Unknown &&
        selectedPremiumStructure !== PremiumStructure.Stepped;

    const getPremiumStructureSection = (
        structure: "stepped" | "level"
    ): React.ReactNode => {
        const iconId = structure === "level" ? "LevelCoverGraph" : "SteppedCoverGraph";
        const title = structure.charAt(0).toUpperCase() + structure.substring(1);

        return (
            <div className={cx("layout-flex-column layout-flex-l gap-l-mobile")}>
                <span className="layout-flex-row text-font-size-ml text-bold text-align--center--mobile gap-m">
                    <Icon id={iconId} className={cx(styles["icon"])} />
                    {title} Cover
                </span>
                {premiumStructureInfo[structure].map((item, ind) => {
                    return (
                        <span
                            key={`${structure}-${ind}`}
                            className="text-line-height-ml text-font-size-s-mobile">
                            <Icon id="SaveTick" className={cx(styles["icon"])} />
                            {item}
                        </span>
                    );
                })}
            </div>
        );
    };

    return (
        <React.Fragment>
            <PremiumStructuresModal
                isOpen={isOpenPremiumStructuresModal}
                onClose={() => {
                    setIsOpenPremiumStructuresModal(false);
                }}
            />
            <p className="layout-hidden-mobile text-align--center text-font-size-ml text-bold">
                Select a Premium Structure
            </p>
            <div
                className={cx(
                    "gap-ml",
                    styles["container"],
                    styles["container-flex"],
                    styles["stepped-border"]
                )}>
                {getPremiumStructureSection("stepped")}
                <Button
                    component="button"
                    size="s"
                    variant="calltoaction"
                    width="content"
                    className={cx("layout-flex-s gap-m", styles["btn-width"], {
                        [styles["btn-selected"]]: !isLevelLifeSelected
                    })}
                    onClick={() => {
                        setSelectedPremiumStructure(PremiumStructure.Stepped);
                        setFieldValue(
                            `${ProductType.Life}.premiumStructure`,
                            PremiumStructure.Stepped
                        );
                    }}>
                    {!isLevelLifeSelected ? (
                        <React.Fragment>
                            <Icon id="WhiteTick" className={cx(styles["icon"])} />
                            {"Selected"}
                        </React.Fragment>
                    ) : (
                        "Select"
                    )}
                </Button>
            </div>
            <p className="text-align--center text-large">OR</p>
            <div className={cx(styles["container"], styles["level-border"])}>
                <div className={cx("width-full", styles["container-flex"])}>
                    {getPremiumStructureSection("level")}
                    <Button
                        component="button"
                        size="s"
                        variant="calltoaction"
                        className={cx("layout-flex-s gap-m", styles["btn-width"], {
                            [styles["btn-selected"]]: isLevelLifeSelected
                        })}
                        onClick={() => {
                            setSelectedPremiumStructure(PremiumStructure.LevelTenYears);
                            setFieldValue(
                                `${ProductType.Life}.premiumStructure`,
                                PremiumStructure.LevelTenYears
                            );
                        }}>
                        {isLevelLifeSelected ? (
                            <React.Fragment>
                                <Icon id="WhiteTick" className={cx(styles["icon"])} />
                                {"Selected"}
                            </React.Fragment>
                        ) : (
                            "Select"
                        )}
                    </Button>
                </div>
                {isLevelLifeSelected && (
                    <div className="layout-flex-column--centered margin-vertical-2xl">
                        <p
                            className={cx(
                                "gap-xl",
                                "text-bold",
                                "text-font-size-s",
                                "text-line-height-ml",
                                "text-align--center--mobile"
                            )}>
                            Select how long you would like to have this cover for.
                        </p>
                        <Toggle
                            name={`${ProductType.Life}.premiumStructure`}
                            options={levelLifeOptions.map((l) => {
                                return {
                                    ...l,
                                    selected: l.id === selectedPremiumStructure
                                };
                            })}
                            className={cx(styles["toggle-mobile"])}
                            onChange={(e) => {
                                setSelectedPremiumStructure(e as PremiumStructure);
                                setFieldValue(
                                    `${ProductType.Life}.premiumStructure`,
                                    e as PremiumStructure
                                );
                            }}
                        />
                    </div>
                )}
            </div>
            <a
                href="#"
                tabIndex={0}
                className={cx(
                    "text-font-size-s",
                    "text-align--center",
                    "layout-flex-row-centered gap-2xl",
                    styles["about-link"]
                )}
                onClick={() => setIsOpenPremiumStructuresModal(true)}>
                <Icon id="Info" size="l" className={cx("gap-pre-l", styles["icon"])} />
                About Premium Structures
            </a>
        </React.Fragment>
    );
};

export default GlobalProductFormLife;
